(() => {
  if (window.humblerBuyAnyTwoReady) return;
  window.humblerBuyAnyTwoReady = true;
  const CONFETTI_SCRIPT_SRC = "https://cdn.jsdelivr.net/npm/canvas-confetti@1/dist/confetti.browser.min.js";

  const parseInteger = (value, fallback = 2) => {
    const parsed = Number.parseInt(String(value ?? ""), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const parseCents = (value) => {
    const parsed = Number.parseInt(String(value ?? "0"), 10);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const parseProductCents = (value) => {
    const cents = parseCents(value);
    return cents >= 100000 ? Math.round(cents / 100) : cents;
  };

  const toBool = (value) => String(value).toLowerCase() === "true";

  const getOrdinal = (number) => {
    const suffixes = ["th", "st", "nd", "rd"];
    const remainder = number % 100;
    return `${number}${suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0]}`;
  };

  class BuyAnyTwoConfigurator extends HTMLElement {
    connectedCallback() {
      this.modal = this.closest("[data-buy-any-two-modal]");
      this.isInModal = Boolean(this.modal);
      if (window.Shopify?.designMode) this.hydrate();
      if (!this.modal?.hidden) this.activate();
    }

    hydrate() {
      const content = this.querySelector("template[data-buy-any-two-content]");
      if (content) content.replaceWith(content.content);
    }

    activate() {
      this.hydrate();
      if (!this.initialized) {
        this.initialize();
      } else {
        this.bindEvents();
        this.setupMobileBarObserver();
        this.render();
      }
    }

    deactivate() {
      if (!this.initialized) return;
      this.closeAllVariantPanels();
      this.eventController?.abort();
      this.eventController = null;
      this.stopUpsellTimer();
      this.mobileBarObserver?.disconnect();
      this.mobileBarObserver = null;
    }

    initialize() {
      if (this.initialized) return;
      this.initialized = true;

      this.selected = new Map();
      this.busy = false;
      this.addingSuccess = false;
      this.requiredItems = Math.max(parseInteger(this.dataset.requiredItems, 2), 2);
      this.offerPrice = this.dataset.offerPrice || "$99";
      this.offerPriceCents = parseCents(this.dataset.offerPriceCents);
      this.currencyCodeEnabled = toBool(this.dataset.currencyCodeEnabled);
      this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
      this.moneyWithCurrencyFormat = this.dataset.moneyWithCurrencyFormat || this.moneyFormat;
      this.upsellEnabled = toBool(this.dataset.upsellEnabled);
      this.upsellPrice = this.dataset.upsellPrice || "$29";
      this.upsellPriceCents = parseCents(this.dataset.upsellPriceCents);
      this.upsellDurationSeconds = Math.max(parseInteger(this.dataset.upsellDurationSeconds, 180), 0);
      this.upsellSelecting = false;
      this.upsellExpired = false;
      this.upsellExpiresAt = 0;
      this.upsellTimerId = null;
      this.upsellVisible = false;

      this.labels = {
        add: this.dataset.addLabel || "Add to set",
        added: this.dataset.addedLabel || "Added",
        remove: this.dataset.removeLabel || "Remove",
        ready: this.dataset.readyLabel || "Ready to add",
        selectMore: this.dataset.selectMoreLabel || "Select more",
        saved: this.dataset.savedLabel || "You saved",
        included: this.dataset.includedLabel || "Price included in the set",
        upsellRow: this.dataset.upsellRowLabel || "Limited-time price",
        adding: this.dataset.addingLabel || "Adding bundle...",
        openingCart: this.dataset.openingCartLabel || "Added. Opening cart..."
      };

      this.els = {
        tabs: Array.from(this.querySelectorAll("[data-buy-any-two-tab]")),
        panels: Array.from(this.querySelectorAll("[data-buy-any-two-panel]")),
        cards: Array.from(this.querySelectorAll("[data-buy-any-two-card]")),
        stepTitle: this.querySelector("[data-buy-any-two-step-title]"),
        clear: this.querySelector("[data-buy-any-two-clear]"),
        list: this.querySelector("[data-buy-any-two-list]"),
        summaryPanel: this.querySelector("[data-buy-any-two-summary-panel]"),
        summaryPanelList: this.querySelector("[data-buy-any-two-summary-panel-list]"),
        cta: this.querySelector("[data-buy-any-two-cta]"),
        savings: this.querySelector("[data-buy-any-two-savings]"),
        savingsPercent: this.querySelector("[data-buy-any-two-savings-percent]"),
        mobileSavings: this.querySelector("[data-buy-any-two-mobile-savings]"),
        mobileSavingsPercent: this.querySelector("[data-buy-any-two-mobile-savings-percent]"),
        mobileCta: this.querySelector("[data-buy-any-two-mobile-cta]"),
        mobileBar: this.querySelector("[data-buy-any-two-mobile-bar]"),
        progressBars: Array.from(this.querySelectorAll("[data-buy-any-two-progress]")),
        totalPrices: Array.from(this.querySelectorAll("[data-buy-any-two-total]")),
        upsells: Array.from(this.querySelectorAll("[data-buy-any-two-upsell]")),
        upsellTimers: Array.from(this.querySelectorAll("[data-buy-any-two-upsell-timer]")),
        upsellStarts: Array.from(this.querySelectorAll("[data-buy-any-two-upsell-start]")),
        upsellChoiceLists: Array.from(this.querySelectorAll("[data-buy-any-two-upsell-list]")),
        error: this.querySelector("[data-buy-any-two-error]")
      };

      this.defaultCtaLabels = new Map();
      [this.els.cta, this.els.mobileCta].forEach((button) => {
        const label = button?.querySelector("span")?.textContent?.trim();
        if (button && label) this.defaultCtaLabels.set(button, label);
      });

      this.els.loadingOverlay = this.createLoadingOverlay();
      this.appendChild(this.els.loadingOverlay);

      this.modal = this.closest("[data-buy-any-two-modal]");
      this.isInModal = Boolean(this.modal);

      this.els.cards.forEach((card) => this.syncCardVariant(card));
      this.bindEvents();
      if (!this.restoreProductPageSelection()) {
        this.activateCategory("all", { scroll: false });
      }
      this.setupMobileBarObserver();
      this.render();
    }

    createLoadingOverlay() {
      const overlay = document.createElement("div");
      overlay.className = "buy-any-two-loading-overlay";
      overlay.setAttribute("aria-live", "polite");
      overlay.setAttribute("aria-hidden", "true");
      overlay.innerHTML = `
        <div class="buy-any-two-loading-overlay__card" role="status">
          <span class="buy-any-two-loading-overlay__spinner" aria-hidden="true"></span>
          <span class="buy-any-two-loading-overlay__eyebrow">Bundle secured</span>
          <strong class="buy-any-two-loading-overlay__title">Adding your set</strong>
          <span class="buy-any-two-loading-overlay__text">Hang tight, your cart is being updated.</span>
          <span class="buy-any-two-loading-overlay__bar" aria-hidden="true"></span>
        </div>
      `;
      return overlay;
    }

    disconnectedCallback() {
      if (this.modal && !this.modal.hidden) this.closeModal();
      else this.deactivate();
    }

    bindEvents() {
      if (this.eventController) return;
      this.eventController = new AbortController();
      const options = { signal: this.eventController.signal };
      this.addEventListener("click", (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        const tab = target.closest("[data-buy-any-two-tab]");
        if (tab) return this.activateCategory(tab.dataset.categoryId);
        if (target.closest("[data-buy-any-two-clear]")) return this.clearSelection();
        if (target.closest("[data-buy-any-two-cta], [data-buy-any-two-mobile-cta]")) return this.addSetToCart();
        if (target.closest("[data-buy-any-two-upsell-start]")) return this.startUpsellSelection();
        const card = target.closest("[data-buy-any-two-card]");
        if (!card) return;
        const option = target.closest("[data-buy-any-two-variant-option]");
        if (option) {
          if (option.dataset.available !== "false") this.selectVariant(card, option);
          return;
        }
        if (target.closest("[data-buy-any-two-variant-trigger]")) return this.toggleVariantPanel(card);
        if (target.closest("[data-buy-any-two-toggle]")) this.toggleCard(card);
      }, options);
      this.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        const option = event.target.closest?.("[data-buy-any-two-variant-option]");
        if (!option || option.dataset.available === "false") return;
        event.preventDefault();
        const card = option.closest("[data-buy-any-two-card]");
        if (card) this.selectVariant(card, option);
      }, options);
      document.addEventListener("click", (event) => {
        if (!this.openVariantPanel || event.target.closest?.("[data-buy-any-two-variant]")) return;
        this.closeAllVariantPanels();
      }, options);
      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && this.openVariantPanel) this.closeAllVariantPanels();
      }, options);
    }

    toggleVariantPanel(card) {
      const trigger = card.querySelector("[data-buy-any-two-variant-trigger]");
      const panel = card.querySelector("[data-buy-any-two-variant-options]");
      if (!trigger || !panel) return;

      const willOpen = panel.hidden;
      this.closeAllVariantPanels();

      if (!willOpen) return;

      panel.hidden = false;
      this.openVariantPanel = panel;
      trigger.setAttribute("aria-expanded", "true");
    }

    closeAllVariantPanels() {
      const panel = this.openVariantPanel;
      if (!panel) return;
      panel.hidden = true;
      panel.parentElement?.querySelector("[data-buy-any-two-variant-trigger]")?.setAttribute("aria-expanded", "false");
      this.openVariantPanel = null;
    }

    selectVariant(card, optionEl) {
      const options = card.querySelectorAll("[data-buy-any-two-variant-option]");
      options.forEach((opt) => {
        const isSelected = opt === optionEl;
        opt.classList.toggle("is-selected", isSelected);
        opt.setAttribute("aria-selected", isSelected ? "true" : "false");
      });

      card.dataset.variantId = optionEl.dataset.value;
      card.dataset.variantTitle = optionEl.dataset.title;
      card.dataset.available = optionEl.dataset.available;
      if (optionEl.dataset.price !== undefined) card.dataset.price = optionEl.dataset.price;
      if (optionEl.dataset.compareAt !== undefined) card.dataset.compareAt = optionEl.dataset.compareAt;

      const current = card.querySelector("[data-buy-any-two-variant-current]");
      if (current) current.textContent = optionEl.dataset.title;

      this.closeAllVariantPanels();

      const key = this.getCardKey(card);
      if (this.selected.has(key)) {
        this.selected.set(key, this.getCardData(card));
      }
      this.render();
    }

    setupMobileBarObserver() {
      if (this.mobileBarObserver || !this.els.mobileBar || typeof IntersectionObserver === "undefined") return;

      this.mobileBarObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries && entries[0];
          this.els.mobileBar.classList.toggle("is-visible", Boolean(entry && entry.isIntersecting));
        },
        { root: null, threshold: 0.01 }
      );

      this.mobileBarObserver.observe(this);
    }

    activateCategory(categoryId, { scroll = true } = {}) {
      const showAll = categoryId === "all";

      this.els.tabs.forEach((tab) => {
        const isActive = tab.dataset.categoryId === categoryId;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      let activePanel = null;
      this.els.panels.forEach((panel) => {
        const isActive = showAll || panel.dataset.categoryId === categoryId;
        panel.classList.toggle("is-active", isActive);
        panel.hidden = !isActive;
        if (!activePanel && isActive) activePanel = panel;
      });

      if (scroll && activePanel) {
        this.scrollContentIntoView(activePanel);
      }
    }

    scrollContentIntoView(target, { behavior = "smooth" } = {}) {
      if (!target) return;

      const stickyHeader = this.querySelector(".buy-any-two__sticky-header");
      const stickyHeight = stickyHeader ? stickyHeader.offsetHeight : 0;

      const modalScroller = this.modal && this.modal.querySelector(".buy-any-two-modal__dialog");

      requestAnimationFrame(() => {
        if (modalScroller) {
          const containerRect = modalScroller.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          const desired = modalScroller.scrollTop + (targetRect.top - containerRect.top) - stickyHeight - 4;
          if (Math.abs(modalScroller.scrollTop - desired) < 4) return;
          modalScroller.scrollTo({ top: Math.max(desired, 0), behavior });
          return;
        }

        const rect = target.getBoundingClientRect();
        const desired = window.scrollY + rect.top - stickyHeight - 4;
        if (Math.abs(window.scrollY - desired) < 4) return;
        window.scrollTo({ top: Math.max(desired, 0), behavior });
      });
    }

    getCardKey(card) {
      return card.dataset.cardKey || card.dataset.productId || String(this.els.cards.indexOf(card));
    }

    getCardData(card) {
      return {
        key: this.getCardKey(card),
        title: card.dataset.productTitle || "",
        url: card.dataset.productUrl || "#",
        image: card.dataset.productImage || "",
        variantId: card.dataset.variantId,
        variantTitle: card.dataset.variantTitle || "",
        price: parseProductCents(card.dataset.price),
        compareAt: parseProductCents(card.dataset.compareAt),
        card
      };
    }

    syncCardVariant(card) {
      const select = card.querySelector("[data-buy-any-two-variant-select]");
      if (!select) return;

      const option = select.selectedOptions && select.selectedOptions[0];
      if (!option) return;

      card.dataset.variantId = option.value;
      card.dataset.variantTitle = option.dataset.title || option.textContent.trim();
      card.dataset.available = option.dataset.available || (option.disabled ? "false" : "true");

      if (option.dataset.price !== undefined) {
        card.dataset.price = option.dataset.price;
      }

      if (option.dataset.compareAt !== undefined) {
        card.dataset.compareAt = option.dataset.compareAt;
      }
    }

    selectProductPageItem(productHandle, variantId) {
      if (!productHandle) return false;

      const card = this.els.cards.find((candidate) => candidate.dataset.productHandle === productHandle);
      if (!card) return false;

      if (variantId) {
        const variantOption = Array.from(card.querySelectorAll("[data-buy-any-two-variant-option]")).find(
          (option) => option.dataset.value === String(variantId) && option.dataset.available !== "false"
        );

        if (variantOption) this.selectVariant(card, variantOption);
      }

      if (String(card.dataset.available).toLowerCase() === "false") return false;

      const cardKey = this.getCardKey(card);
      const cardData = this.getCardData(card);

      if (this.selected.has(cardKey)) {
        this.selected.set(cardKey, cardData);
      } else {
        const previousSelections = Array.from(this.selected.entries()).filter(([key]) => key !== cardKey);
        this.selected.clear();
        this.selected.set(cardKey, cardData);
        previousSelections.slice(0, Math.max(this.requiredItems - 1, 0)).forEach(([key, item]) => {
          this.selected.set(key, item);
        });
      }

      const panel = card.closest("[data-buy-any-two-panel]");
      this.activateCategory(panel?.dataset.categoryId || "all", { scroll: false });
      this.render();
      return true;
    }

    restoreProductPageSelection() {
      const params = new URLSearchParams(window.location.search);
      if (params.get("setbuilder") !== "1") return false;

      return this.selectProductPageItem(params.get("first"), params.get("variant"));
    }

    clearError() {
      if (!this.els.error) return;
      this.els.error.textContent = "";
    }

    setError(message) {
      if (!this.els.error) return;
      this.els.error.textContent = message;
    }

    isReady() {
      return this.selected.size >= this.requiredItems;
    }

    hasUpsellItem() {
      return this.upsellEnabled && this.selected.size > this.requiredItems;
    }

    canUseUpsellSlot() {
      return this.upsellEnabled && !this.upsellExpired && (this.upsellSelecting || this.hasUpsellItem());
    }

    getSelectionLimit() {
      return this.requiredItems + (this.canUseUpsellSlot() ? 1 : 0);
    }

    getVisibleSummaryCount() {
      return Math.max(this.requiredItems, this.selected.size);
    }

    getCurrentOfferPriceCents() {
      return this.offerPriceCents + (this.hasUpsellItem() ? this.upsellPriceCents : 0);
    }

    getCurrentOfferDisplay() {
      if (!this.hasUpsellItem()) return this.offerPrice;
      return this.formatMoney(this.getCurrentOfferPriceCents());
    }

    toggleCard(card) {
      if (this.busy) return;
      this.clearError();

      const key = this.getCardKey(card);
      if (this.selected.has(key)) {
        this.selected.delete(key);
        if (this.selected.size <= this.requiredItems) {
          this.upsellSelecting = false;
        }
        if (this.selected.size < this.requiredItems) {
          this.resetUpsellTimer();
        }
        this.render();
        return;
      }

      const selectionLimit = this.getSelectionLimit();
      if (this.selected.size >= selectionLimit) {
        const message = this.upsellSelecting
          ? "This bonus offer is limited to 1 extra item."
          : `This offer is limited to ${this.requiredItems} items.`;
        this.setError(message);
        return;
      }

      if (String(card.dataset.available).toLowerCase() === "false") {
        this.setError("This item is unavailable.");
        return;
      }

      this.selected.set(key, this.getCardData(card));
      if (this.upsellSelecting && this.selected.size > this.requiredItems) {
        this.upsellSelecting = false;
      }
      this.render();
    }

    clearSelection() {
      if (this.busy) return;

      this.selected.clear();
      this.upsellSelecting = false;
      this.resetUpsellTimer();
      this.clearError();
      this.render();
    }

    renderStepTitle() {
      if (!this.els.stepTitle) return;

      if (this.upsellSelecting) {
        this.els.stepTitle.textContent = this.dataset.upsellSelectTitle || "Choose your bonus item";
        return;
      }

      const selectedCount = this.selected.size;
      if (selectedCount >= this.requiredItems) {
        this.els.stepTitle.textContent = this.dataset.summaryTitle || "Summary";
        return;
      }

      this.els.stepTitle.textContent = `Select your ${getOrdinal(selectedCount + 1)} item`;
    }

    renderCards() {
      const selectionLimit = this.getSelectionLimit();
      const selectedKeys = Array.from(this.selected.keys());

      this.els.cards.forEach((card) => {
        const key = this.getCardKey(card);
        const isSelected = this.selected.has(key);
        const selectedIndex = selectedKeys.indexOf(key);
        const isAvailable = String(card.dataset.available).toLowerCase() !== "false";
        const isOverLimit = !isSelected && this.selected.size >= selectionLimit;
        const toggles = card.querySelectorAll("[data-buy-any-two-toggle]");
        const label = card.querySelector("[data-buy-any-two-toggle-label]");
        const check = card.querySelector(".buy-any-two-card__check");

        card.classList.toggle("is-selected", isSelected);
        card.classList.toggle("is-disabled", isOverLimit);

        toggles.forEach((toggle) => {
          toggle.disabled = this.busy || !isAvailable || isOverLimit;
          toggle.setAttribute("aria-pressed", isSelected ? "true" : "false");
        });

        if (label) label.textContent = isSelected ? this.labels.added : this.labels.add;
        if (check) {
          check.textContent = isSelected ? String(selectedIndex + 1) : "";
          check.setAttribute("aria-label", isSelected ? `Selected item ${selectedIndex + 1}` : "");
        }
      });
    }

    renderSummary() {
      const selectedItems = Array.from(this.selected.values());
      const summaryCount = this.getVisibleSummaryCount();

      if (this.els.list) {
        this.els.list.innerHTML = "";
        for (let index = 0; index < summaryCount; index += 1) {
          this.els.list.appendChild(this.buildSummaryRow(selectedItems[index], index, "compact"));
        }
      }

      if (this.els.summaryPanelList) {
        this.els.summaryPanelList.innerHTML = "";
        for (let index = 0; index < summaryCount; index += 1) {
          this.els.summaryPanelList.appendChild(this.buildSummaryRow(selectedItems[index], index, "panel"));
        }
      }
    }

    buildSummaryRow(item, index, variant) {
      const isPanel = variant === "panel";
      const baseClass = isPanel ? "buy-any-two__summary-row" : "buy-any-two-summary-item";
      const li = document.createElement("li");
      li.className = baseClass;

      if (!item) {
        li.classList.add("is-empty");
        const thumb = document.createElement("div");
        thumb.className = `${baseClass}__thumb`;
        thumb.setAttribute("aria-hidden", "true");

        const info = document.createElement("div");
        info.className = `${baseClass}__info`;

        const title = document.createElement("span");
        title.className = `${baseClass}__title`;
        title.textContent = this.labels.selectMore;

        info.appendChild(title);

        li.appendChild(thumb);
        li.appendChild(info);
        return li;
      }

      const thumb = document.createElement("div");
      thumb.className = `${baseClass}__thumb`;
      if (item.image) {
        const image = document.createElement("img");
        image.src = item.image;
        image.alt = item.title;
        image.loading = "lazy";
        thumb.appendChild(image);
      }
      li.appendChild(thumb);

      const info = document.createElement("div");
      info.className = `${baseClass}__info`;

      const title = document.createElement(isPanel ? "p" : "a");
      title.className = `${baseClass}__title`;
      title.textContent = item.title;
      if (!isPanel) title.href = item.url;
      info.appendChild(title);

      const variantTitle = item.variantTitle && item.variantTitle !== "Default Title" ? item.variantTitle : "";
      if (variantTitle) {
        const variantEl = document.createElement("span");
        variantEl.className = `${baseClass}__variant`;
        variantEl.textContent = variantTitle;
        info.appendChild(variantEl);
      }

      li.appendChild(info);

      if (isPanel) {
        const priceBlock = document.createElement("div");
        priceBlock.className = `${baseClass}__price`;
        const isUpsellItem = index >= this.requiredItems;

        const includedLabel = document.createElement("p");
        includedLabel.className = `${baseClass}__included`;
        includedLabel.textContent = isUpsellItem ? this.labels.upsellRow : this.labels.included;
        priceBlock.appendChild(includedLabel);

        if (isUpsellItem) {
          const upsellPrice = document.createElement("strong");
          upsellPrice.className = `${baseClass}__offer`;
          upsellPrice.textContent = this.upsellPrice;
          priceBlock.appendChild(upsellPrice);
        }

        const original = item.compareAt > item.price ? item.compareAt : item.price;
        if (original > 0) {
          const original_el = document.createElement("s");
          original_el.className = `${baseClass}__original`;
          original_el.textContent = this.formatMoney(original);
          priceBlock.appendChild(original_el);
        }

        li.appendChild(priceBlock);

        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = `${baseClass}__remove`;
        remove.setAttribute("aria-label", `${this.labels.remove} ${item.title}`);
        remove.innerHTML = "&times;";
        remove.addEventListener("click", () => {
          if (this.busy) return;
          this.selected.delete(item.key);
          if (this.selected.size <= this.requiredItems) {
            this.upsellSelecting = false;
          }
          if (this.selected.size < this.requiredItems) {
            this.resetUpsellTimer();
          }
          this.clearError();
          this.render();
        });
        li.appendChild(remove);
      } else {
        li.appendChild(this.createRemoveButton(item));
      }

      return li;
    }

    formatMoney(cents) {
      const value = (cents || 0) / 100;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: value % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(value);
    }

    createRemoveButton(item) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "buy-any-two-summary-item__remove";
      button.innerHTML = "&times;";
      button.setAttribute("aria-label", `${this.labels.remove} ${item.title}`);
      button.addEventListener("click", () => {
        if (this.busy) return;

        this.selected.delete(item.key);
        if (this.selected.size <= this.requiredItems) {
          this.upsellSelecting = false;
        }
        if (this.selected.size < this.requiredItems) {
          this.resetUpsellTimer();
        }
        this.clearError();
        this.render();
      });

      return button;
    }

    startUpsellSelection() {
      if (!this.upsellEnabled || this.upsellExpired || this.hasUpsellItem() || !this.isReady()) return;

      this.clearError();
      this.upsellSelecting = true;
      this.ensureUpsellTimer();
      this.render();
      this.activateCategory("all");

      const activePanel = this.querySelector(".buy-any-two__panel.is-active") || this.querySelector("[data-buy-any-two-panel]");
      this.scrollContentIntoView(activePanel || this);
    }

    addUpsellCard(card) {
      if (!this.upsellEnabled || this.upsellExpired || this.hasUpsellItem() || !this.isReady()) return;
      if (String(card.dataset.available).toLowerCase() === "false") return;

      this.clearError();
      this.upsellSelecting = true;
      this.ensureUpsellTimer();
      this.selected.set(this.getCardKey(card), this.getCardData(card));
      this.upsellSelecting = false;
      this.render();
      this.scrollContentIntoView(this.els.summaryPanel || this);
    }

    focusUpsellCard(card) {
      if (!this.upsellEnabled || this.upsellExpired || this.hasUpsellItem() || !this.isReady()) return;

      this.clearError();
      this.upsellSelecting = true;
      this.ensureUpsellTimer();

      const panel = card.closest("[data-buy-any-two-panel]");
      this.activateCategory(panel?.dataset.categoryId || "all", { scroll: false });
      this.render();

      requestAnimationFrame(() => {
        this.scrollContentIntoView(card);
        const focusTarget = card.querySelector("[data-buy-any-two-toggle]");
        focusTarget?.focus?.();
      });
    }

    getUpsellCards() {
      const selectedProductIds = new Set(Array.from(this.selected.values()).map((item) => String(item.card?.dataset.productId || "")));
      const seenProductIds = new Set();

      return this.els.cards.filter((card) => {
        const productId = String(card.dataset.productId || "");
        if (!productId || selectedProductIds.has(productId) || seenProductIds.has(productId)) return false;
        if (String(card.dataset.available).toLowerCase() === "false") return false;

        seenProductIds.add(productId);
        return true;
      });
    }

    buildUpsellChoice(card) {
      const choice = document.createElement("div");
      choice.className = "buy-any-two-upsell-choice";

      const mediaButton = document.createElement("button");
      mediaButton.type = "button";
      mediaButton.className = "buy-any-two-upsell-choice__media";
      mediaButton.setAttribute("aria-label", `Add ${card.dataset.productTitle || "item"} as bonus item`);
      mediaButton.addEventListener("click", () => this.addUpsellCard(card));

      const image = document.createElement("img");
      image.className = "buy-any-two-upsell-choice__image";
      image.src = card.dataset.productImage || "";
      image.alt = card.dataset.productTitle || "";
      image.loading = "lazy";
      mediaButton.appendChild(image);
      choice.appendChild(mediaButton);

      const addButton = document.createElement("button");
      addButton.type = "button";
      addButton.className = "buy-any-two-upsell-choice__icon";
      addButton.setAttribute("aria-label", `Add ${card.dataset.productTitle || "item"} as bonus item`);
      addButton.textContent = "+";
      addButton.addEventListener("click", () => this.addUpsellCard(card));
      choice.appendChild(addButton);

      return choice;
    }

    renderUpsellChoices(showUpsell) {
      if (this.els.upsellChoiceLists.length === 0) return;

      const cards = showUpsell ? this.getUpsellCards().slice(0, 10) : [];
      this.els.upsellChoiceLists.forEach((list) => {
        list.innerHTML = "";
        list.hidden = cards.length === 0;

        cards.forEach((card) => {
          list.appendChild(this.buildUpsellChoice(card));
        });
      });
    }

    ensureUpsellTimer() {
      if (this.modal?.hidden || !this.upsellEnabled || this.upsellExpired) return;
      if (this.upsellDurationSeconds <= 0) return;

      if (this.upsellExpiresAt > 0) {
        if (!this.upsellTimerId) {
          this.upsellTimerId = window.setInterval(() => this.handleUpsellTick(), 1000);
        }
        this.handleUpsellTick();
        return;
      }

      this.upsellExpiresAt = Date.now() + this.upsellDurationSeconds * 1000;
      this.upsellTimerId = window.setInterval(() => this.handleUpsellTick(), 1000);
      this.handleUpsellTick();
    }

    stopUpsellTimer() {
      if (!this.upsellTimerId) return;

      window.clearInterval(this.upsellTimerId);
      this.upsellTimerId = null;
    }

    resetUpsellTimer() {
      this.stopUpsellTimer();
      this.upsellExpired = false;
      this.upsellExpiresAt = 0;
      this.upsellVisible = false;
      this.updateUpsellTimers(0);
    }

    handleUpsellTick() {
      const remainingSeconds = Math.max(0, Math.ceil((this.upsellExpiresAt - Date.now()) / 1000));
      this.updateUpsellTimers(remainingSeconds);

      if (remainingSeconds > 0) return;

      this.stopUpsellTimer();
      if (this.hasUpsellItem()) return;

      this.upsellExpired = true;
      this.upsellSelecting = false;
      this.render();
    }

    updateUpsellTimers(remainingSeconds) {
      const minutes = Math.floor(remainingSeconds / 60);
      const seconds = String(remainingSeconds % 60).padStart(2, "0");
      const text = remainingSeconds > 0 ? `${minutes}:${seconds}` : "";
      const progress = this.upsellDurationSeconds > 0 ? (remainingSeconds / this.upsellDurationSeconds) * 100 : 0;

      this.els.upsellTimers.forEach((timer) => {
        timer.textContent = text;
        timer.classList.toggle("is-urgent", remainingSeconds > 0 && remainingSeconds <= 30);
      });

      this.els.upsells.forEach((upsell) => {
        upsell.style.setProperty("--buy-any-two-upsell-progress", `${Math.max(0, Math.min(100, progress))}%`);
      });
    }

    loadConfettiLibrary() {
      if (typeof window.confetti === "function") return Promise.resolve(window.confetti);

      if (window.buyAnyTwoConfettiPromise) return window.buyAnyTwoConfettiPromise;

      window.buyAnyTwoConfettiPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = CONFETTI_SCRIPT_SRC;
        script.async = true;
        script.onload = () => resolve(window.confetti);
        script.onerror = reject;
        document.head.appendChild(script);
      });

      return window.buyAnyTwoConfettiPromise;
    }

    celebrateUpsell() {
      if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

      this.els.upsells.forEach((upsell) => {
        if (upsell.hidden) return;

        upsell.classList.add("is-pop");
        window.setTimeout(() => upsell.classList.remove("is-pop"), 500);
      });

      this.loadConfettiLibrary()
        .then((confetti) => {
          if (this.modal?.hidden || typeof confetti !== "function") return;

          confetti({
            particleCount: 120,
            spread: 90,
            startVelocity: 38,
            origin: { y: 0.55 },
            colors: ["#24cc75", "#d62828", "#ffffff", "#111111"],
            scalar: 0.9,
            ticks: 220,
            zIndex: 2147483647,
            disableForReducedMotion: true
          });

          window.setTimeout(() => {
            confetti({
              particleCount: 70,
              angle: 60,
              spread: 60,
              origin: { x: 0, y: 0.75 },
              colors: ["#24cc75", "#d62828", "#ffffff"],
              scalar: 0.8,
              zIndex: 2147483647,
              disableForReducedMotion: true
            });
            confetti({
              particleCount: 70,
              angle: 120,
              spread: 60,
              origin: { x: 1, y: 0.75 },
              colors: ["#24cc75", "#d62828", "#ffffff"],
              scalar: 0.8,
              zIndex: 2147483647,
              disableForReducedMotion: true
            });
          }, 180);
        })
        .catch(() => {});
    }

    renderUpsell() {
      if (!this.upsellEnabled || this.els.upsells.length === 0) return;

      const showUpsell = this.isReady() && !this.hasUpsellItem() && !this.upsellExpired;
      if (showUpsell) {
        this.ensureUpsellTimer();
      } else if (this.selected.size < this.requiredItems) {
        this.resetUpsellTimer();
      }

      if (this.hasUpsellItem()) {
        this.stopUpsellTimer();
      }

      this.els.upsells.forEach((upsell) => {
        upsell.hidden = !showUpsell;
      });
      if (showUpsell && !this.upsellVisible) {
        this.celebrateUpsell();
      }
      this.upsellVisible = showUpsell;
      this.renderUpsellChoices(showUpsell);

      this.els.upsellStarts.forEach((button) => {
        button.disabled = this.busy || this.upsellSelecting || !showUpsell;
      });
    }

    renderGiftPromo() {
      // hb-gift-ui-v3: show the free gift rows once this set qualifies (same rule as the cart)
      const minItems = parseInteger(this.dataset.giftMinItems, 0);
      if (!minItems) return;
      const spendCents = parseCents(this.dataset.giftSpendCents);
      const giftProductId = String(this.dataset.giftProductId || "");
      let count = 0;
      this.selected.forEach((item) => {
        if (String(item.card?.dataset.productId || "") !== giftProductId) count += 1;
      });
      const unlocked = count >= minItems && this.getCurrentOfferPriceCents() >= spendCents;
      this.querySelectorAll("[data-hb-gift-unlocked]").forEach((element) => {
        element.hidden = !unlocked;
      });
    }

    renderControls() {
      const isReady = this.isReady();
      if (this.els.clear) this.els.clear.disabled = this.busy || this.selected.size === 0;

      [this.els.cta, this.els.mobileCta].forEach((button) => {
        if (!button) return;

        const label = button.querySelector("span");
        const defaultLabel = this.defaultCtaLabels.get(button) || this.labels.ready;
        button.disabled = this.busy || !isReady;
        button.classList.toggle("is-loading", this.busy && !this.addingSuccess);
        button.classList.toggle("is-success", this.addingSuccess);
        button.setAttribute("aria-busy", this.busy ? "true" : "false");

        if (label) {
          label.textContent = this.addingSuccess ? this.labels.openingCart : this.busy ? this.labels.adding : defaultLabel;
        }
      });
    }

    renderLoadingOverlay() {
      if (!this.els.loadingOverlay) return;

      const title = this.els.loadingOverlay.querySelector(".buy-any-two-loading-overlay__title");
      const text = this.els.loadingOverlay.querySelector(".buy-any-two-loading-overlay__text");
      const eyebrow = this.els.loadingOverlay.querySelector(".buy-any-two-loading-overlay__eyebrow");

      this.els.loadingOverlay.classList.toggle("is-visible", this.busy);
      this.els.loadingOverlay.classList.toggle("is-success", this.addingSuccess);
      this.els.loadingOverlay.setAttribute("aria-hidden", this.busy ? "false" : "true");

      if (eyebrow) eyebrow.textContent = this.addingSuccess ? "Ready" : "Bundle secured";
      if (title) title.textContent = this.addingSuccess ? "Opening your cart" : "Adding your set";
      if (text) {
        text.textContent = this.addingSuccess
          ? "Your bundle is in. Opening your cart now."
          : "Hang tight, your cart is being updated.";
      }
    }

    getSavingsPercent() {
      const currentOfferPriceCents = this.getCurrentOfferPriceCents();
      if (currentOfferPriceCents <= 0 || this.selected.size === 0) return 0;

      let originalTotal = 0;
      this.selected.forEach((item) => {
        const price = item.compareAt > item.price ? item.compareAt : item.price;
        originalTotal += price || 0;
      });

      if (originalTotal <= currentOfferPriceCents) return 0;

      const percent = Math.round(((originalTotal - currentOfferPriceCents) / originalTotal) * 100);
      return Math.max(0, Math.min(99, percent));
    }

    renderSavings() {
      const percent = this.getSavingsPercent();
      const visible = percent > 0 && this.selected.size > 0;
      const text = `${percent}%!`;

      if (this.els.savings) {
        this.els.savings.hidden = !visible;
      }
      if (this.els.savingsPercent) {
        this.els.savingsPercent.textContent = text;
      }
      if (this.els.mobileSavings) {
        this.els.mobileSavings.hidden = !visible;
      }
      if (this.els.mobileSavingsPercent) {
        this.els.mobileSavingsPercent.textContent = text;
      }
    }

    renderTotals() {
      const currentOfferPriceCents = this.getCurrentOfferPriceCents();
      let retailCents = 0;
      this.selected.forEach((item) => {
        const price = item.compareAt > item.price ? item.compareAt : item.price;
        retailCents += price || 0;
      });

      const savingsCents = Math.max(0, retailCents - currentOfferPriceCents);
      const percent = this.getSavingsPercent();
      const hasItems = this.selected.size > 0;

      this.els.totalPrices.forEach((total) => {
        total.textContent = this.getCurrentOfferDisplay();
      });

      const retailRow = this.querySelector("[data-buy-any-two-retail-row]");
      const retailTotalEl = this.querySelector("[data-buy-any-two-retail-total]");
      if (retailRow) retailRow.hidden = !hasItems || retailCents <= 0;
      if (retailTotalEl) retailTotalEl.textContent = retailCents > 0 ? this.formatMoney(retailCents) : "";

      const savingsRow = this.querySelector("[data-buy-any-two-savings-row]");
      const savingsAmountEl = this.querySelector("[data-buy-any-two-savings-amount]");
      const savingsPercentEl = this.querySelector("[data-buy-any-two-savings-amount-percent]");

      const showSavings = hasItems && savingsCents > 0;
      if (savingsRow) savingsRow.hidden = !showSavings;
      if (savingsAmountEl) savingsAmountEl.textContent = showSavings ? `\u2212 ${this.formatMoney(savingsCents)}` : "";
      if (savingsPercentEl) savingsPercentEl.textContent = percent > 0 ? `${percent}%` : "";
    }

    renderProgress() {
      if (this.els.progressBars.length === 0) return;

      const totalSteps = Math.max(this.requiredItems + 1, 2);
      const currentStep = Math.min(this.selected.size, this.requiredItems);
      const fillPercent = (currentStep / (totalSteps - 1)) * 100;

      this.els.progressBars.forEach((bar) => {
        const fill = bar.querySelector("[data-buy-any-two-progress-fill]");
        if (fill) fill.style.width = `${fillPercent}%`;

        const steps = bar.querySelectorAll("[data-step]");
        steps.forEach((step) => {
          const stepIndex = parseInteger(step.dataset.step, 0);
          step.classList.toggle("is-active", stepIndex === currentStep);
          step.classList.toggle("is-complete", stepIndex < currentStep);
        });
      });
    }

    renderSummaryState() {
      const wasSummary = this.classList.contains("is-summary");
      const isSummary = this.isReady() && !this.upsellSelecting;
      this.classList.toggle("is-summary", isSummary);

      if (this.els.summaryPanel) {
        this.els.summaryPanel.hidden = !isSummary;
      }

      this.els.panels.forEach((panel) => {
        if (isSummary) {
          panel.hidden = true;
          return;
        }

        const isActiveTab = panel.classList.contains("is-active");
        panel.hidden = !isActiveTab;
      });

      const tabsContainer = this.querySelector(".buy-any-two__tabs");
      if (tabsContainer) {
        tabsContainer.hidden = isSummary;
      }

      if (isSummary && !wasSummary) {
        this.scrollContentIntoView(this.els.summaryPanel || this);
      } else if (!isSummary && wasSummary) {
        const activePanel = this.querySelector(".buy-any-two__panel.is-active");
        if (activePanel) {
          this.scrollContentIntoView(activePanel);
        }
      }
    }

    exitSummary() {
      if (this.busy) return;
      const lastKey = Array.from(this.selected.keys()).pop();
      if (lastKey) {
        this.selected.delete(lastKey);
      }
      this.clearError();
      this.render();
    }

    render() {
      this.classList.toggle("is-adding", this.busy);
      this.classList.toggle("is-added", this.addingSuccess);
      this.renderLoadingOverlay();
      this.renderStepTitle();
      this.renderCards();
      this.renderSummary();
      this.renderSavings();
      this.renderTotals();
      this.renderUpsell();
      this.renderGiftPromo();
      this.renderProgress();
      this.renderSummaryState();
      this.renderControls();
    }

    getBundleOfferLabel(hasUpsellItem = this.hasUpsellItem()) {
      const baseOffer = `Buy any ${this.requiredItems} for ${this.offerPrice}`;
      if (!hasUpsellItem) return baseOffer;

      return `${baseOffer} + 1 bonus item for ${this.upsellPrice}`;
    }

    getLineItemProperties(bundleId, index, totalItems) {
      const isUpsellItem = this.upsellEnabled && index >= this.requiredItems;
      const properties = {
        _bundle_id: bundleId,
        _bundle_offer: this.getBundleOfferLabel(totalItems > this.requiredItems),
        _bundle_item: `${index + 1} of ${totalItems}`
      };

      if (isUpsellItem) {
        properties._bundle_upsell = this.upsellPrice;
        properties._bundle_upsell_cents = String(this.upsellPriceCents);
      }

      return properties;
    }

    async addSetToCart() {
      if (this.busy) return;
      this.clearError();
      this.addingSuccess = false;

      if (!this.isReady()) {
        this.setError(`Select ${this.requiredItems} items to add this set.`);
        return;
      }

      const selectedItems = Array.from(this.selected.values());
      const bundleId = `${this.dataset.sectionId || "buy-any-two"}-${Date.now()}-${Math.random()
        .toString(16)
        .slice(2)}`;
      const cartDrawer = document.querySelector("cart-drawer");
      const sections = this.getCartSections(cartDrawer);
      if (cartDrawer && typeof cartDrawer.setActiveElement === "function") {
        cartDrawer.setActiveElement(document.activeElement);
      }

      const payload = {
        items: selectedItems.map((item, index) => ({
          id: Number(item.variantId),
          quantity: 1,
          properties: this.getLineItemProperties(bundleId, index, selectedItems.length)
        })),
        sections_url: window.location.pathname
      };

      if (sections.length > 0) {
        payload.sections = sections;
      }

      this.busy = true;
      this.render();

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data && data.description ? data.description : "Could not add this set to cart.");
        }

        this.selected.clear();
        this.upsellSelecting = false;
        this.resetUpsellTimer();
        this.addingSuccess = true;
        this.render();

        await this.openCart(cartDrawer, data, selectedItems[0]);
      } catch (error) {
        this.addingSuccess = false;
        this.setError(error && error.message ? error.message : "Something went wrong.");
      } finally {
        this.busy = false;
        this.addingSuccess = false;
        this.render();
      }
    }

    getCartSections(cartDrawer) {
      if (window.customCart || !cartDrawer || typeof cartDrawer.getSectionsToRender !== "function") return [];

      return cartDrawer.getSectionsToRender().map((section) => section.id);
    }

    async openCart(cartDrawer, data, firstItem) {
      if (window.customCart && typeof window.customCart.update === "function") {
        try {
          await window.customCart.update();
        } catch (error) {
          this.closeModal();
          window.location.assign(window.routes?.cart_url || "/cart");
          return;
        }
        this.closeModal();
        return;
      }

      if (cartDrawer && typeof cartDrawer.renderContents === "function" && data.sections) {
        this.closeModal();
        cartDrawer.renderContents({
          ...data,
          id: firstItem && firstItem.productId ? Number(firstItem.productId) : data.id
        });
        return;
      }

      document.dispatchEvent(new CustomEvent("cart:refresh"));

      if (cartDrawer && typeof cartDrawer.open === "function") {
        this.closeModal();
        cartDrawer.open();
        return;
      }

      if (window.routes && window.routes.cart_url) {
        window.location.href = window.routes.cart_url;
        return;
      }

      window.location.href = "/cart";
    }

    closeModal() {
      if (!this.modal) return;

      this.deactivate();
      this.modal.hidden = true;
      document.documentElement.classList.remove("buy-any-two-modal-open");
      document.body.classList.remove("buy-any-two-modal-open");
    }
  }

  const openModal = (modal) => {
    if (!modal) return;

    modal.hidden = false;
    modal.querySelector("buy-any-two-configurator")?.activate?.();
    document.documentElement.classList.add("buy-any-two-modal-open");
    document.body.classList.add("buy-any-two-modal-open");

    const dialog = modal.querySelector(".buy-any-two-modal__dialog");
    const focusTarget =
      modal.querySelector("[data-buy-any-two-dialog-close]") || modal.querySelector("[data-buy-any-two-close]") || dialog;
    requestAnimationFrame(() => focusTarget?.focus?.());
  };

  const closeModal = (modal) => {
    if (!modal) return;

    modal.querySelector("buy-any-two-configurator")?.deactivate?.();
    modal.hidden = true;
    document.documentElement.classList.remove("buy-any-two-modal-open");
    document.body.classList.remove("buy-any-two-modal-open");
  };

  document.addEventListener("click", (event) => {
    const openTrigger = event.target.closest("[data-buy-any-two-open]");
    if (openTrigger) {
      const modalTarget = openTrigger.dataset.buyAnyTwoOpen;
      const modal =
        document.querySelector(`[data-buy-any-two-modal="${modalTarget}"]`) || document.getElementById(modalTarget);

      if (!modal) return;

      event.preventDefault();
      openModal(modal);
      const configurator = modal.querySelector("buy-any-two-configurator");
      configurator?.selectProductPageItem?.(openTrigger.dataset.productHandle, openTrigger.dataset.variantId);
      return;
    }

    const closeTrigger = event.target.closest("[data-buy-any-two-close]");
    if (closeTrigger) {
      closeModal(closeTrigger.closest("[data-buy-any-two-modal]"));
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;

    const modal = document.querySelector("[data-buy-any-two-modal]:not([hidden])");
    closeModal(modal);
  });

  if (!customElements.get("buy-any-two-configurator")) {
    customElements.define("buy-any-two-configurator", BuyAnyTwoConfigurator);
  }

  const openRequestedBuilder = () => {
    const params = new URLSearchParams(window.location.search);
    const targetsBuilder = window.location.hash === "#buy-any-two-builder";
    if (params.get("setbuilder") !== "1" && !targetsBuilder) return;

    openModal(document.querySelector("#buy-any-two-builder") || document.querySelector("[data-buy-any-two-modal]"));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", openRequestedBuilder, { once: true });
  } else {
    openRequestedBuilder();
  }

  window.addEventListener("pageshow", openRequestedBuilder);
  document.addEventListener("shopify:block:select", (event) => {
    if (!window.Shopify?.designMode) return;
    const modal = Array.from(document.querySelectorAll("[data-buy-any-two-modal]")).find(
      (candidate) => candidate.dataset.buyAnyTwoModal === event.detail?.sectionId
    );
    if (!modal) return;
    openModal(modal);
    const configurator = modal.querySelector("buy-any-two-configurator");
    if (event.detail?.blockId) configurator?.activateCategory?.(event.detail.blockId, { scroll: false });
  });
})();
